import React, { useState, useEffect, useRef } from "react";
import { saveOrUpdateTemplate } from "../../utils/Utility";
import { useUser } from "../../contexts/UserContext";
import { useAdminContext } from "../../contexts/AdminContext.js";
import {
  ackTemplate,
  transmittalTemplate,
  secResTemplate,
  legalAdviceTemplate,
} from "../Constants";
import Loader from "../../utils/Loader";
import { formatOldUrl, decryptData } from "../../utils/cloudApi";
import Select from "react-select";
import { selectStyles } from "../Constants";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import ImagePickerModal from "../../utils/ImagePicker.js";

const EditSpecialTemplateModal = ({
  template,
  isOpen,
  onClose,
  onSave,
  templateType,
}) => {
  const { user, getRecipients, privateKeyInMemory } = useUser();
  const { isViewingOtherUser, selectedUserData } = useAdminContext();
  const currentUser = isViewingOtherUser ? selectedUserData : user;

  const defaultData =
    templateType === "ack"
      ? ackTemplate
      : templateType === "trans"
      ? transmittalTemplate
      : templateType === "secres"
      ? secResTemplate
      : legalAdviceTemplate;

  const [decryptedTemplate, setDecryptedTemplate] = useState({});
  const [header, setHeader] = useState(defaultData.header);
  const [headerColor, setHeaderColor] = useState(defaultData.headerColor);
  const [tableHeaderColor, setTableHeaderColor] = useState(
    defaultData.tableHeaderColor
  );
  const [tableHeaderTextColor, setTableHeaderTextColor] = useState(
    defaultData.tableHeaderTextColor
  );

  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [logo, setLogo] = useState("");
  const [footer, setFooter] = useState("");
  const [logoDims, setLogoDims] = useState({
    width: 0,
    height: 0,
  });
  const [footerDims, setFooterDims] = useState({
    width: 0,
    height: 0,
  });
  const [from, setFrom] = useState(defaultData.from);
  const [body1, setBody1] = useState("");
  const [body2, setBody2] = useState("");
  const [notarizationType, setNotarizationType] = useState(
    defaultData.notarizationType || ""
  ); // 'jurat' | 'non-jurat'
  const [notarizationStatus, setNotarizationStatus] = useState(
    defaultData.notarizationStatus || "non-notarized"
  ); // 'notarized' | 'non-notarized'
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(0);
  const logoInputRef = useRef(null);
  const footerInputRef = useRef(null);

  const [previews, setPreviews] = useState({
    logoPreview: null,
    footerPreview: null,
  });

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  const notarizationStatusOptions = [
    { value: "notarized", label: "Notarized" },
    { value: "non-notarized", label: "Non-Notarized" },
  ];

  const notarizationTypeOptions = [
    { value: "jurat", label: "Jurat" },
    { value: "non-jurat", label: "Non-Jurat" },
  ];

  // Get template type display name
  const getTemplateTypeName = () => {
    if (templateType === "ack") return "Acknowledgement Receipt";
    if (templateType === "trans") return "Transmittal Letter";
    if (templateType === "secres") return "Secretarial Resolution";
    if (templateType === "legal") return "Legal Advice";
    return "Template";
  };

  useEffect(() => {
    const loadTemplate = async () => {
      if (template) {
        const decrypted = await decryptData(
          template,
          user.uid,
          privateKeyInMemory
        );

        setHeader(decrypted.header || defaultData.header);
        setLogo(decrypted.logo || null);
        setFooter(decrypted.footer || null);
        setFrom(decrypted.from || { name: "", address: "", contact: "" });
        setNotarizationStatus(decrypted.notarizationStatus || "");
        setNotarizationType(decrypted.notarizationType || "");
        setLastInvoiceNumber(decrypted.lastInvoiceNumber || 0);
        setTableHeaderColor(decrypted.tableHeaderColor || [43, 112, 115]);
        setTableHeaderTextColor(
          decrypted.tableHeaderTextColor || [255, 255, 255]
        );
        setHeaderColor(decrypted.headerColor || [0, 0, 0]);
        setBody1(decrypted.body1 || "");
        setBody2(decrypted.body2 || "");

        setDecryptedTemplate(decrypted);
      }
    };

    loadTemplate();
  }, [template, defaultData, user]);

  const handleSave = async () => {
    setLoading(true);
    setLoadingText("Saving template...");

    try {
      // save the new files and save to firebase through utility function first
      const updatedTemplate = {
        ...decryptedTemplate,
        header,
        from,
        createdBy: currentUser.uid,
        logo,
        footer,
        invoiceNo: lastInvoiceNumber,
        headerColor: headerColor,
        tableHeaderColor: tableHeaderColor,
        tableHeaderTextColor: tableHeaderTextColor,
        tempType: templateType,
        body1,
        body2,
      };

      if (templateType === "ack" || templateType === "secres") {
        updatedTemplate.notarizationStatus = notarizationStatus;
        updatedTemplate.notarizationType = notarizationType;
      }

      const recipients = await getRecipients();

      let newTemplate;
      if (!decryptedTemplate || !decryptedTemplate.id) {
        // new template
        newTemplate = await saveOrUpdateTemplate(
          null,
          updatedTemplate,
          false,
          templateType,
          recipients
        );
      } else {
        // update template
        newTemplate = await saveOrUpdateTemplate(
          decryptedTemplate.id,
          updatedTemplate,
          true,
          templateType,
          recipients
        );
      }

      onSave(newTemplate);
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const handleImageFieldClick = (field) => {
    setCurrentImageField(field);
    setImagePickerOpen(true);
  };

  const handleImageSelect = (selectedValue) => {
    const isFile = selectedValue instanceof File;

    if (isFile) {
      // New file upload
      const previewUrl = URL.createObjectURL(selectedValue);

      if (currentImageField === "logo") {
        if (previews.logoPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(previews.logoPreview);
        }
        setPreviews((prev) => ({ ...prev, logoPreview: previewUrl }));
        setLogo(selectedValue);
      } else if (currentImageField === "footer") {
        if (previews.footerPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(previews.footerPreview);
        }
        setPreviews((prev) => ({ ...prev, footerPreview: previewUrl }));
        setFooter(selectedValue);
      }
    } else {
      // console.log(selectedValue);
      // Existing image URL
      if (currentImageField === "logo") {
        setPreviews((prev) => ({ ...prev, logoPreview: selectedValue }));
        setLogo(selectedValue);
      } else if (currentImageField === "footer") {
        setPreviews((prev) => ({ ...prev, footerPreview: selectedValue }));
        setFooter(selectedValue);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 py-6 flex items-center justify-center z-40">
      {loading && <Loader text={loadingText} />}

      {imagePickerOpen && (
        <ImagePickerModal
          isOpen={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
          userId={currentUser.uid}
          currentImageUrl={currentImageField === "logo" ? logo : footer}
        />
      )}

      <div className="bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 border text-color rounded-lg shadow-xl w-full max-w-4xl h-full flex flex-col">
        <h2 className="text-2xl font-bold pt-8 px-8">
          {!decryptedTemplate || !decryptedTemplate.id ? "New " : "Edit "}{" "}
          {getTemplateTypeName()} Template
        </h2>

        <div className="custom-divider"></div>

        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          {/* Template Header */}
          <div className="flex flex-row w-full">
            <div className="flex-1 px-4">
              <label htmlFor="header" className="block text-sm">
                Document Title
              </label>
              <input
                id="header"
                type="text"
                value={header}
                onChange={(e) => setHeader(e.target.value)}
                className="w-full p-3 border rounded-lg custom-input outline-none"
              />
            </div>
            <div className="flex-full px-4">
              <label htmlFor="invoiceNo" className="block text-sm">
                Current{" "}
                {templateType === "ack" ? "Acknowledgement" : "Transmittal"} No
              </label>
              <input
                id="invoiceNo"
                type="number"
                value={lastInvoiceNumber}
                onChange={(e) =>
                  setLastInvoiceNumber(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="w-full p-3 border rounded-lg custom-input outline-none"
              />
            </div>
          </div>

          {(templateType === "ack" || templateType === "secres") && (
            <>
              {/* Acknowledgement Types/configurations */}
              <div className="bg-white/10 p-4 rounded-lg space-y-4">
                <h3 className="font-bold text-lg">Acknowledgement Type</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="notStat" className="block text-sm">
                      Notarization Status
                    </label>
                    <Select
                      id="notStat"
                      value={notarizationStatusOptions.find(
                        (opt) => opt.value === notarizationStatus
                      )}
                      onChange={(selected) =>
                        setNotarizationStatus(selected.value)
                      }
                      options={notarizationStatusOptions}
                      styles={selectStyles}
                      classNamePrefix="react-select"
                    />
                  </div>

                  {notarizationStatus === "notarized" && (
                    <div>
                      <label htmlFor="notType" className="block text-sm">
                        Notarization Type
                      </label>
                      <Select
                        id="notType"
                        value={notarizationTypeOptions.find(
                          (opt) => opt.value === notarizationType
                        )}
                        onChange={(selected) =>
                          setNotarizationType(selected.value)
                        }
                        options={notarizationTypeOptions}
                        styles={selectStyles}
                        classNamePrefix="react-select"
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Company Banner */}
          <div className="flex items-start gap-6 bg-white/10 p-4 rounded-lg">
            <div className="flex-1">
              <label htmlFor="banner" className="block text-sm">
                Company Banner
              </label>
              <button
                type="button"
                onClick={() => handleImageFieldClick("logo")}
                className="w-full p-3 border rounded-lg custom-input text-left hover:bg-white/5"
              >
                {logo ? "Change Image" : "Select Image"}
              </button>

              {logoDims.width > 0 && (
                <p className="mt-1 text-xs text-center text-gray-200">
                  Dimensions: {logoDims.width}px × {logoDims.height}px
                </p>
              )}
            </div>
            {(previews.logoPreview || logo) && (
              <div className="flex flex-col items-center gap-2">
                <img
                  src={previews.logoPreview || formatOldUrl(logo)}
                  alt="Company Logo Preview"
                  className="max-h-32 h-auto max-w-sm border dark:border-gray-600 rounded-lg shadow-md"
                  onLoad={(e) =>
                    setLogoDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() => {
                    if (previews.logoPreview?.startsWith("blob:")) {
                      URL.revokeObjectURL(previews.logoPreview);
                    }
                    setPreviews((prev) => ({ ...prev, logoPreview: null }));
                    setLogo(null);
                    if (logoInputRef.current) {
                      logoInputRef.current.value = "";
                    }
                    setLogoDims({ width: 0, height: 0 });
                  }}
                  className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Footer Banner */}
          <div className="flex items-start gap-6 bg-white/10 p-4 rounded-lg">
            <div className="flex-1">
              <label htmlFor="footer" className="block text-sm">
                Footer Banner
              </label>
              <button
                type="button"
                onClick={() => handleImageFieldClick("footer")}
                className="w-full p-3 border rounded-lg custom-input text-left hover:bg-white/5"
              >
                {footer ? "Change Image" : "Select Image"}
              </button>

              {footerDims.width > 0 && (
                <p className="mt-1 text-xs text-center text-gray-200">
                  Dimensions: {footerDims.width}px × {footerDims.height}px
                </p>
              )}
            </div>
            {(previews.footerPreview || footer) && (
              <div className="flex flex-col items-center gap-2">
                <img
                  src={previews.footerPreview || formatOldUrl(footer)}
                  alt="Footer Preview"
                  className="max-h-32 h-auto max-w-sm border dark:border-gray-600 rounded-lg shadow-md"
                  onLoad={(e) =>
                    setFooterDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
                  }
                />

                <button
                  type="button"
                  onClick={() => {
                    if (previews.footerPreview?.startsWith("blob:")) {
                      URL.revokeObjectURL(previews.footerPreview);
                    }
                    setPreviews((prev) => ({ ...prev, footerPreview: null }));
                    setFooter(null);
                    if (footerInputRef.current) {
                      footerInputRef.current.value = "";
                    }
                    setFooterDims({ width: 0, height: 0 });
                  }}
                  className="bg-red-400 dark:bg-red-500 text-color text-sm px-2 py-1 rounded hover:bg-red-500 dark:hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Company Information */}
          <div className="bg-white/10 p-4 rounded-lg space-y-4">
            <h3 className="font-bold text-lg">Issuing Company</h3>

            <div className="flex flex-row w-full space-x-2">
              <div className="flex-1">
                <label htmlFor="from-name" className="block text-sm">
                  Company Name
                </label>
                <input
                  id="from-name"
                  type="text"
                  value={from.name}
                  onChange={(e) =>
                    setFrom((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full p-2 border rounded-lg custom-input"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="from-contact" className="block text-sm">
                  Contact
                </label>
                <input
                  id="from-contact"
                  type="text"
                  value={from.contact}
                  onChange={(e) =>
                    setFrom((prev) => ({ ...prev, contact: e.target.value }))
                  }
                  className="w-full p-2 border rounded-lg custom-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="from-address" className="block text-sm">
                Address
              </label>
              <input
                id="from-address"
                type="text"
                value={from.address}
                onChange={(e) =>
                  setFrom((prev) => ({ ...prev, address: e.target.value }))
                }
                className="w-full p-2 border rounded-lg custom-input"
              />
            </div>
          </div>

          {/* Body */}
          <div className="bg-white/10 p-4 rounded-lg space-y-4">
            <h3 className="font-bold text-lg">Content(s) Template</h3>

            <div>
              <label htmlFor="body1" className="block text-xs mb-1">
                Opening
              </label>

              <div className="bg-tier-secondary/20 text-xs italic p-2 mb-2 rounded-lg flex flex-row gap-2">
                <span className="material-symbols-outlined">info</span>
                <span>
                  Note: The first line should contain the salutation.
                  <br />
                  In the generated PDF, this line will not be indented, while
                  the following paragraphs will be.
                </span>
              </div>

              <ReactQuill
                id="body1"
                key={`body1`}
                // ref={body1Quill.ref}
                value={body1 || ""}
                onChange={(value) => setBody1(value)}
                modules={quillModules}
                placeholder="Enter first section paragraph/s before the table..."
                theme="snow"
                className="bg-white/10"
              />
            </div>

            <div>
              <label htmlFor="body2" className="block text-xs mb-2">
                Closing (Optional)
              </label>
              <ReactQuill
                id="body2"
                key={`body2`}
                value={body2 || ""}
                onChange={(value) => setBody2(value)}
                modules={quillModules}
                placeholder="Enter second section paragraph/s after the table..."
                theme="snow"
                className="bg-white/10"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 p-4 border-t border-gray-300 dark:border-gray-600 bg-tier-primary dark:bg-tier-primary-dark">
          <button onClick={onClose} className="btn-action-white">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-action">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSpecialTemplateModal;
